import { Box, ButtonBase, useTheme } from "@mui/material";
import { datagridPageSizeOptions } from "../../../../newapi/global/filter/DataGridGeneralOptions";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { TabContextProvider } from "../../../../contexts/TabContext";
import ShRhDatagrid from "../../../theme/rhdatagrid/ShRhDatagrid";
import { ORDER_FILTER_KEYS } from "../../DataGrid/Filter";
import { useEffect, useRef, useState } from "react";
import { rhChartColorsDark, rhChartColorsLight } from "../chartFilterEnums";
import { GridFilterProvider } from "../../../../contexts/GridFilterContext";
import { orderColumns } from "../columns/orderColumns";
import Shbutton from "../../../theme/buttons/Shbutton";
import { reporthubChartOptionKeys } from "../chartOptions";
import { rhInboundColumns } from "../columns/rhInboundColumns";
import { rhReturnColumns } from "../columns/rhReturnColumns";
import ShTextSkeleton from "../../../theme/skeleton/ShTextSkeleton";
import Text from "../../../theme/text/Text";
import { rhShipmentColumns } from "../columns/rhShipmentColumns";
import { rhWarehouseLocationColumns } from "../columns/rhWarehouseLocationColumns";
import { rhFinancialColumns } from "../columns/rhFinancialColumns";
import SHModal from "../../../theme/Modal/SHModal";
import Subheading from "../../../theme/text/Subheading";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { MTooManyPalletSpots } from "./MTooManyPalletSpots";
import SidepanelLoader from "../../../theme/sidepanel2/SidepanelLoader";
import { useSelector } from "react-redux";
import { ENV_TYPES } from "../../Sidebar";

const SpOrderDetails = SidepanelLoader(() => import("../../Sidepanels/SpOrderDetails/SpOrderDetails"));
const SpInboundDetails = SidepanelLoader(() => import("../../Sidepanels/SpInboundDetails/SpInboundDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../Sidepanels/SpReturnDetails/SpReturnDetails"));
const SpLocationDetails = SidepanelLoader(() => import("../../Sidepanels/SpLocationDetails/SpLocationDetails"));
const SpInvoiceDetails = SidepanelLoader(() => import("../../Sidepanels/SpInvoiceDetails/SpInvoiceDetails"));

export const RhDataTable = (props) => {
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data,setData] = useState(null);

    const [page,setPage] = useState(props.page ?? 1);
    const [pageSize,setPageSize] = useState(datagridPageSizeOptions[0]);

    // Sync local page state when parent's page prop changes (e.g., when filters reset page to 1)
    useEffect(() => {
        if (props.page !== undefined && props.page !== page) {
            setPage(props.page);
        }
    }, [props.page]);

    const [columnStrategy,setColumnStrategy] = useState(null);

    // Track sort model changes from the grid filter context
    const prevSortModelRef = useRef(null);
    const handleFilterObjectChanged = (filterObject) => {
        if (filterObject && props.onSortChange) {
            const currentSort = filterObject.sortModel || {};
            const prevSort = prevSortModelRef.current;

            // Only trigger callback if sort actually changed
            if (JSON.stringify(currentSort) !== JSON.stringify(prevSort)) {
                prevSortModelRef.current = currentSort;
                // Pass null if empty object (no sort)
                const sortToPass = currentSort.field ? currentSort : null;
                props.onSortChange(sortToPass);
            }
        }
    };

    useEffect(() => {
        if(props.data){
            setData(props.data);
        }
    },[props.data])

    const [open,setOpen] = useState(false);
    const [warehouseRow,setWarehouseRow] = useState(null);

    const generateTables = () => {
        switch(props.currentReport?.type) {
            case reporthubChartOptionKeys.ORDERS:
                return orderColumns(i18n,theme,t,colors,columnStrategy);
            case reporthubChartOptionKeys.INBOUNDS:
                return rhInboundColumns(i18n,theme,t,colors,columnStrategy);
            case reporthubChartOptionKeys.RETURNS:
                return rhReturnColumns(i18n,theme,t,colors,columnStrategy);
            case reporthubChartOptionKeys.SHIPMENTS:
                return rhShipmentColumns(i18n,theme,t,colors,columnStrategy);
            case reporthubChartOptionKeys.WAREHOUSELOCATIONS:
                return rhWarehouseLocationColumns(i18n,theme,t,colors,columnStrategy, (row) => {
                    setOpen(true);
                    setWarehouseRow(row);
                });
            case reporthubChartOptionKeys.FINANCIAL:
                return rhFinancialColumns(i18n,theme,t,colors,columnStrategy);
            default: 
                return [
                    {field: "data", headerName: t("data"),flex:1,
                        renderCell: (params) => {
                            if(params.row.skeleton) {
                                return <ShTextSkeleton/>
                            }
                            // console.log(params.row);
                            return <Text>Row </Text>
                        }
                    }
                ]
        }
    }

    const envType = useSelector((state) => state.settings.environment.selectedEnvironmentType);

    // State for different sidepanels
    const [id, setId] = useState();
    const idRef = useRef(id);

    const [ordersSpIsExtended, setOrdersSpIsExtended] = useState(false);
    const [startLoadingOrderDetails, setStartLoadingOrderDetails] = useState(false);

    const [inboundsSpIsExtended, setInboundsSpIsExtended] = useState(false);
    const [spInboundLoading, setSpInboundLoading] = useState(false);

    const [returnsSpIsExtended, setReturnsSpIsExtended] = useState(false);
    const [spReturnLoading, setSpReturnLoading] = useState(false);

    const [locationsSpIsExtended, setLocationsSpIsExtended] = useState(false);
    const [spLocationLoading, setSpLocationLoading] = useState(false);

    const [invoicesSpIsExtended, setInvoicesSpIsExtended] = useState(false);
    const [spInvoiceLoading, setSpInvoiceLoading] = useState(false);

    const getDetails = (params) => {
        if(!params) return;

        const newId = parseInt(params.row.id);
        if(idRef.current !== newId) {
            setId(newId);
            idRef.current = newId;
        }

        switch(props.currentReport?.type) {
            case reporthubChartOptionKeys.ORDERS:
                if(!startLoadingOrderDetails) {
                    setStartLoadingOrderDetails(true);
                }
                setOrdersSpIsExtended(true);
                break;
            case reporthubChartOptionKeys.INBOUNDS:
                if(!spInboundLoading) {
                    setSpInboundLoading(true);
                }
                setInboundsSpIsExtended(true);
                break;
            case reporthubChartOptionKeys.RETURNS:
                if(!spReturnLoading) {
                    setSpReturnLoading(true);
                }
                setReturnsSpIsExtended(true);
                break;
            case reporthubChartOptionKeys.WAREHOUSELOCATIONS:
                if(!spLocationLoading) {
                    setSpLocationLoading(true);
                }
                setLocationsSpIsExtended(true);
                break;
            case reporthubChartOptionKeys.FINANCIAL:
                if(!spInvoiceLoading) {
                    setSpInvoiceLoading(true);
                }
                setInvoicesSpIsExtended(true);
                break;
            case reporthubChartOptionKeys.SHIPMENTS:
                // No sidepanel for shipments
                break;
            default:
                break;
        }
    }

    useEffect(() => {
        if(props.chartInfo && props.currentReport && props.currentReport.filter) {
            if(props.chartInfo.series.length > 0) {
                let keyToHighlight;
                const series = props.chartInfo.series.map((item,idx) => {
                        return {
                            idx,
                            name: item.name,
                            color: {
                                dark: rhChartColorsDark[idx],
                                light: rhChartColorsLight[idx],
                            },
                        }
                });


                Object.entries(props.currentReport?.filter).map((item) => {
                    const actualKey= item[0];
                    const actualValue= item[1];
                    if(actualValue.primary){
                        keyToHighlight=actualKey;
                    }
                });
                setColumnStrategy({
                    key:keyToHighlight,
                    series
                })
            } else {
                setColumnStrategy(null);

            }
        }
    },[props.chartInfo,props.currentReport?.filter]);


    return (
        <Box
            sx={{
                // mt:5,
                width:1,
                minHeight:"calc(100% - 189.5px)",
                background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                position:"relative",
                zIndex:2,

                pt:4,
                px:4,
                display:"flex",
                // alignItems:"flex-start",

                "&:before" : {
                    content: "''",
                    display:"block",
                    width:"32px",
                    height:"32px",
                    position:"absolute",
                    left:0,
                    zIndex:1,
                    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    top:"0px",
                    height:"48px",
                    width:"24px",
                    borderTopLeftRadius:"24px",
                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                    transition:"250ms"
                },

                "&:after" : {
                    content: "''",
                    display:"block",
                    width:"32px",
                    height:"32px",
                    position:"absolute",
                    top:0,
                    zIndex:1,
                    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    right:0,
                    height:"48px",
                    width:"24px",
                    borderTopLeftRadius:"24px",
                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                    transform:"scaleX(-1)",
                    transition:"250ms"
                },

                "@media screen and (max-width: 1024px)" : {
                    mt:3,
                },
                "@media screen and (max-width: 47.9375em)" : {
                    flexDirection:"column",
                    "&:after, &:before" : {
                        top:"-0px"
                    }
                }
            }}
        >
            <TabContextProvider>
                <GridFilterProvider activeChild={"chart"}>
                    <Box
                        sx={{
                            width: 1,
                            height: 1,
                            opacity: props.isLoading ? 0.5 : 1,
                            transition: "opacity 200ms ease-in-out",
                            pointerEvents: props.isLoading ? "none" : "auto",
                        }}
                    >
                        <ShRhDatagrid
                            key={`${theme.palette.mode}-${JSON.stringify(columnStrategy)}-${pageSize}`}
                            isLoading={props.isLoading}
                            isSuccess={props.isSuccess}
                            isError={props.isError}
                            page={page}
                            pageSize={pageSize}
                            title={t("overview")}
                            columns={generateTables()}
                            onRowClick={getDetails}
                            setActivePage={(page) => {
                                setPage(page);
                                props.setPage(page);
                                // props.handlePagination(page,pageSize);

                            }}
                            setPageSize={(pageSize) => {
                                setPageSize(pageSize)
                                // props.handlePagination(page,pageSize);
                                props.setPageSize(pageSize);
                            }}
                            // rows={data}
                            pageData={data?? null}
                            gridOptions={{
                                export:true,
                                disableCustomActionsOnPhone: true,
                                isSidepanel: true,
                                filter: [ORDER_FILTER_KEYS.STATE,ORDER_FILTER_KEYS.STREAM,ORDER_FILTER_KEYS.SHIPPER,ORDER_FILTER_KEYS.PRODUCT, ORDER_FILTER_KEYS.RELATION,],
                            }}
                            selectedActions={props.selectedActions ?? null}
                            filterObjectChanged={handleFilterObjectChanged}
                        />
                    </Box>
                </GridFilterProvider>
            </TabContextProvider>
            <MTooManyPalletSpots
                open={open}
                handleClose={() => setOpen(false)}
                {...warehouseRow}
            />

            {startLoadingOrderDetails &&
                <SpOrderDetails
                    isExtended={ordersSpIsExtended}
                    setIsExtended={setOrdersSpIsExtended}
                    orderId={id}
                    relation={envType === ENV_TYPES[200]}
                    canReserveOrderLines={envType === ENV_TYPES[200]}
                />
            }

            {spInboundLoading &&
                <SpInboundDetails
                    isExtended={inboundsSpIsExtended}
                    setIsExtended={setInboundsSpIsExtended}
                    inboundId={id}
                />
            }

            {spReturnLoading &&
                <SpReturnDetails
                    isExtended={returnsSpIsExtended}
                    setIsExtended={setReturnsSpIsExtended}
                    returnId={id}
                />
            }

            {spLocationLoading &&
                <SpLocationDetails
                    isExtended={locationsSpIsExtended}
                    setIsExtended={setLocationsSpIsExtended}
                    locationId={id}
                />
            }

            {spInvoiceLoading &&
                <SpInvoiceDetails
                    isExtended={invoicesSpIsExtended}
                    setIsExtended={setInvoicesSpIsExtended}
                    invoiceId={id}
                />
            }

        </Box>
    )
}
